/**
 * Brand Colour Contrast Checking
 *
 * The backend does not check contrast, and the Go client does not silently
 * correct it: it applies below-AA pairs anyway and drops functionally invisible
 * ones in favour of the Toptracer default. So warning the person entering the
 * values is this UI's job.
 *
 * The WCAG ratio itself comes from the repo's existing implementation in
 * `qr-customize/utils/colorValidation`. Note `validateColorContrast` there is
 * hardcoded against white and is not usable for arbitrary pairs.
 *
 * THRESHOLDS AND DEFAULTS BELOW MIRROR THE GO CLIENT.
 * Source: pocket-monitor-sap/src/branding/theme.ts (AA_CONTRAST,
 * INVISIBLE_CONTRAST, DEFAULT_BACKGROUND, DEFAULT_FOREGROUND) and
 * pocket-monitor-sap/src/branding/color.ts (INK, WHITE).
 *
 * They are duplicated rather than fetched because this app must warn without
 * talking to the Go client. They are four stable constants describing a
 * published accessibility standard and the app's own default palette, not a
 * protocol; if they drift, the failure is a warning that is slightly too eager
 * or too lax rather than a broken feature.
 *
 * Requirements: 4.1, 4.2, 4.3, 4.5
 */

import { calculateContrastRatio } from '../../qr-customize/utils/colorValidation';
import type { BrandColorSlot, BrandColorsDraft } from '../models/domainModels';
import { COLOR_PAIRS, isBrandColor, type BackgroundSlot } from './brandColors';

/** WCAG AA for normal text. Below this a pair is reported but still applied. */
const AA_CONTRAST = 4.5;

/**
 * Below this a pair is not merely marginal but unreadable, and the Go client
 * drops both halves in favour of the Toptracer default.
 */
const INVISIBLE_CONTRAST = 2;

/** The app's default ink. */
const INK = '#15222E';
const WHITE = '#FFFFFF';

/**
 * What the Go client's stylesheet already paints, per pair.
 *
 * Needed to tell a fallback that works from one that does not: a club setting
 * only a dark `header` keeps the default dark header text and renders it
 * invisible, whereas a club setting a light `raised` is served perfectly well by
 * the default ink.
 *
 * `header`'s default foreground is black rather than the app's ink, because
 * header text inherits the browser default and `--on-header` tokenises that
 * rather than changing it.
 */
const PAIR_DEFAULTS: Readonly<
  Record<BackgroundSlot, { readonly background: string; readonly foreground: string }>
> = {
  accent: { background: '#1A9ED5', foreground: WHITE },
  header: { background: WHITE, foreground: '#000000' },
  surface: { background: '#F3F4F5', foreground: INK },
  raised: { background: WHITE, foreground: INK },
};

/** How a pair's contrast should be presented to the operator. */
export type ContrastSeverity = 'ok' | 'below_aa' | 'will_be_ignored';

export interface ContrastFinding {
  /** The slot pair, e.g. `surface/onSurface`. */
  readonly pair: string;
  /** The editing group's label, e.g. `Page Background`. */
  readonly label: string;
  readonly ratio: number;
  readonly severity: ContrastSeverity;
  /**
   * True when only one half of the pair was set and the other half came from
   * the Toptracer default. Worth surfacing, because the operator did not choose
   * the colour the warning is about.
   */
  readonly comparedAgainstDefault: boolean;
}

/**
 * A slot's value, or `null` when it is unset or not yet a colour.
 *
 * An incomplete value is treated as absent rather than as an error: the field's
 * own validation reports that, and a checker that threw could not be called on
 * every keystroke.
 */
const colorOf = (colors: BrandColorsDraft, slot: BrandColorSlot): string | null => {
  const value = colors[slot];
  return isBrandColor(value) ? value : null;
};

const severityOf = (ratio: number): ContrastSeverity => {
  if (ratio < INVISIBLE_CONTRAST) return 'will_be_ignored';
  if (ratio < AA_CONTRAST) return 'below_aa';
  return 'ok';
};

/**
 * Contrast findings for every pair with at least one half set.
 *
 * A pair with neither half set produces no finding: there is nothing the
 * operator chose, and the Toptracer default is known to be legible.
 */
export function checkContrast(colors: BrandColorsDraft): readonly ContrastFinding[] {
  return COLOR_PAIRS.flatMap((pair) => {
    const background = colorOf(colors, pair.background);
    const foreground = colorOf(colors, pair.foreground);

    if (background === null && foreground === null) return [];

    const comparedAgainstDefault = background === null || foreground === null;
    const defaults = PAIR_DEFAULTS[pair.background];
    const ratio = calculateContrastRatio(
      background ?? defaults.background,
      foreground ?? defaults.foreground
    );

    return [
      {
        pair: `${pair.background}/${pair.foreground}`,
        label: pair.label,
        ratio,
        severity: severityOf(ratio),
        comparedAgainstDefault,
      },
    ];
  });
}
